// ────────────────────────────────────────────────────────────────────
// SiteMapScreen — Level 2: Plant Integrity Dashboard
//
// Route:  /sites/:plantId
// The plant-level question is "which of my assets need attention?", so this
// is an asset worklist (risk-sorted, filterable) beside a plot-plan pane —
// the same map + master-detail pattern as the Atlas, one level down. Area is
// a filter, not a mandatory drill level. The plot pane is adaptive: it shows
// the site layout when assets are positioned, else an honest placeholder.
// ────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState, lazy, Suspense } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, MapPin, AlertTriangle } from "lucide-react";
import { apiClient } from "@/lib/api";
import type { Asset, Area } from "@/lib/types";
import SitePlot2D from "@/components/SitePlot2D";
import { PlantTriagePanel } from "@/components/PlantTriagePanel";
import { CompliancePanel } from "@/components/CompliancePanel";
import { PidCanvas, hotspotAssetTag, type PidHotspot } from "@/mockup3d/PidCanvas";
import { AssetNavSidebar } from "@/components/AssetNavSidebar";
import { PidHoverCard } from "@/components/PidHoverCard";

// The 3D view IS the curated Production Deck (R3F) — lazy so its heavy 3D stack
// only loads when 3D is selected. (Replaced the auto-generated SiteScene3D.)
const Mockup3DScreen = lazy(() => import("@/screens/Mockup3DScreen"));

export default function SiteMapScreen(): JSX.Element {
  const { plantId } = useParams<{ plantId: string }>();
  const navigate = useNavigate();
  const id = Number(plantId);
  // View follows ?view=2d|3d (e.g. the asset-page shortcut), else the Headline —
  // the plant's default front-door view. Synced so back/forward + same-route URL
  // changes apply; the switcher still flips `view` locally without the URL.
  const [searchParams] = useSearchParams();
  const qv = searchParams.get("view");
  const paramView: "2d" | "3d" | "runway" | "compliance" =
    qv === "2d" ? "2d" : qv === "runway" ? "runway" : qv === "compliance" ? "compliance" : "3d";
  const [view, setView] = useState<"2d" | "3d" | "runway" | "compliance">(paramView);
  useEffect(() => { setView(paramView); }, [paramView]);

  const plantQ = useQuery({ queryKey: ["plant", id], queryFn: () => apiClient.plant(id), enabled: !!id });
  const assetsQ = useQuery({ queryKey: ["assets", { plant_id: id }], queryFn: () => apiClient.assets({ plant_id: id }), enabled: !!id });
  const areasQ = useQuery({ queryKey: ["areas", { plant_id: id }], queryFn: () => apiClient.areas({ plant_id: id }), enabled: !!id });

  const plant = plantQ.data;
  const allAssets = assetsQ.data ?? [];
  const allAreas: Area[] = areasQ.data ?? [];

  const positioned = useMemo(() => allAssets.filter((a) => a.plot_x_m != null && a.plot_y_m != null).length, [allAssets]);

  // Real asset health for the live integrity map (spatial views), keyed by
  // lower-cased tag. "" stays "" → the views render it neutral, never green.
  const statusByTag = useMemo(() => {
    const m: Record<string, string> = {};
    for (const a of allAssets) m[a.tag.toLowerCase()] = a.worst_cml_status;
    return m;
  }, [allAssets]);

  // Areas that have a registered plot-plan image AND valid real-world dimensions.
  const plottedAreas = useMemo<Area[]>(
    () =>
      allAreas.filter(
        (a) => !!a.plot_plan_image_path && a.plot_plan_width_m != null && a.plot_plan_height_m != null,
      ),
    [allAreas],
  );

  // Which area is selected in the plot-plan pane — default to first plottable area.
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);

  // Keep selectedAreaId in sync when areas load or the list changes.
  const selectedArea = useMemo<Area | null>(() => {
    if (plottedAreas.length === 0) return null;
    const found = plottedAreas.find((a) => a.id === selectedAreaId);
    return found ?? plottedAreas[0];
  }, [plottedAreas, selectedAreaId]);

  // Assets that belong to the currently selected area (prevents cross-area mixing).
  const areaAssets = useMemo<Asset[]>(
    () => (selectedArea ? allAssets.filter((a) => a.area_id === selectedArea.id) : []),
    [allAssets, selectedArea],
  );

  // ── Hover-to-inspect on the P&ID integrity map ──────────────────────
  // hoverHotspot drives the symbol highlight + which asset; mouse drives only
  // the card position (PidCanvas is memo'd so it stays out of the mouse-move
  // render path). The click handler resolves equipment AND lines via the
  // shared tag resolver, so a line click drills in too.
  const [hoverHotspot, setHoverHotspot] = useState<PidHotspot | null>(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const hoverAsset = useMemo<Asset | null>(() => {
    if (!hoverHotspot) return null;
    const tag = hotspotAssetTag(hoverHotspot);
    return tag ? allAssets.find((a) => a.tag.toLowerCase() === tag) ?? null : null;
  }, [hoverHotspot, allAssets]);
  const onPidClick = useCallback((h: PidHotspot) => {
    const tag = hotspotAssetTag(h);
    const a = tag ? areaAssets.find((x) => x.tag.toLowerCase() === tag) : null;
    if (a) navigate(`/assets/${a.id}`);
  }, [areaAssets, navigate]);

  if (plantQ.isPending) return <CenterStatus>Loading plant…</CenterStatus>;
  if (plantQ.isError || !plant) {
    return <CenterStatus tone="err">Plant not found. <Link to="/atlas" className="underline" style={{ color: "var(--accent-deep)" }}>Back to Atlas</Link></CenterStatus>;
  }

  return (
    <div className="absolute inset-0 flex overflow-hidden" style={{ background: "var(--paper)" }}>

      {/* worklist sidebar — full-height left rail, same position as the asset
          page so the rail doesn't jump when navigating between them */}
      <AssetNavSidebar
        assets={allAssets}
        plantId={id}
        loading={assetsQ.isPending}
        emptyHint="No assets registered for this plant."
      />

      {/* right column: breadcrumb + plant header + plot pane */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

      {/* breadcrumb */}
      <div className="px-6 h-10 border-b flex items-center justify-between shrink-0" style={{ borderColor: "var(--hairline)" }}>
        <div className="font-mono uppercase flex items-center gap-2" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
          <Link to="/atlas" className="hover:text-[var(--accent)] transition-colors">ATLAS</Link>
          <span className="opacity-40">/</span>
          <span style={{ color: "var(--ink-1)" }}>{plant.code}</span>
        </div>
        <Link to="/atlas" className="font-mono uppercase flex items-center gap-1.5 hover:text-[var(--accent)] transition-colors" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
          <ArrowLeft size={11} /> BACK TO ATLAS
        </Link>
      </div>

      {/* plant header + exception KPIs */}
      <header className="px-8 py-5 border-b" style={{ borderColor: "var(--hairline)" }}>
        <div className="flex items-end justify-between gap-8">
          <div className="min-w-0">
            <div className="font-mono uppercase mb-2" style={{ fontSize: 10.5, letterSpacing: ".24em", color: "var(--accent-deep)", fontWeight: 600 }}>
              / PLANT · {plant.client_name}
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-mono" style={{ fontSize: 22, letterSpacing: ".08em", color: "var(--accent-deep)", fontWeight: 700 }}>{plant.code}</span>
              <h1 className="font-sans font-medium m-0" style={{ fontSize: 30, letterSpacing: "-.015em", color: "var(--ink-1)", lineHeight: 1.1 }}>{plant.name}</h1>
            </div>
            <div className="flex items-center gap-4 mt-2 font-mono text-xs" style={{ color: "var(--ink-3)" }}>
              {plant.location && <span className="flex items-center gap-1.5"><MapPin size={11} style={{ color: "var(--accent)" }} />{plant.location}</span>}
              {plant.lat != null && plant.lon != null && <span className="tabular-nums">{Number(plant.lat).toFixed(3)}°N {Number(plant.lon).toFixed(3)}°E</span>}
              {plant.installation_year && <span>Since {plant.installation_year}</span>}
            </div>
          </div>
          <div className="grid grid-cols-3 shrink-0 shadow-soft" style={{ border: "1px solid var(--hairline)", background: "var(--paper-2)" }}>
            <KpiCell label="AREAS" value={plant.area_count} />
            <KpiCell label="ASSETS" value={plant.asset_count} />
            <KpiCell label="CMLs<min" value={plant.cmls_below_min || 0} crit />
          </div>
        </div>
      </header>

      {/* plot pane (primary) — adaptive: placeholder until assets positioned */}
      <main className="flex-1 flex min-h-0">
        <section className="flex-1 flex flex-col min-w-0" style={{ background: "var(--paper-2)" }}>
          <div className="px-5 py-2.5 border-b flex items-center justify-between font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".2em", color: "var(--ink-3)", borderColor: "var(--hairline)" }}>
            {/* view switcher — both forms are live integrity maps */}
            <div className="flex items-center gap-1" style={{ fontSize: 9, letterSpacing: ".12em" }}>
              <span style={{ color: "var(--ink-3)" }}>/ Integrity map</span>
              {selectedArea && positioned > 0 && ([["3d", "3D PLOT PLAN"], ["2d", "PID"], ["runway", "RUNWAY"], ["compliance", "COMPLIANCE"]] as const).map(([k, label]) => (
                <button key={k} onClick={() => setView(k)} className="px-2 py-0.5 border transition-colors ml-1"
                  style={{ borderColor: "var(--hairline)", background: view === k ? "var(--accent)" : "transparent", color: view === k ? "var(--ink-1)" : "var(--ink-3)" }}>
                  {label}
                </button>
              ))}
            </div>
            {/* plot context — area selector + positioned count */}
            <div className="flex items-center gap-3">
              {plottedAreas.length > 1 && (
                <div className="flex items-center gap-1" style={{ fontSize: 9, letterSpacing: ".1em" }}>
                  {plottedAreas.map((a) => (
                    <button key={a.id} onClick={() => setSelectedAreaId(a.id)} className="px-2 py-0.5 border transition-colors"
                      style={{ borderColor: "var(--hairline)", background: selectedArea?.id === a.id ? "rgba(var(--accent-rgb) / 0.12)" : "transparent", color: selectedArea?.id === a.id ? "var(--accent-deep)" : "var(--ink-3)" }}>
                      {a.code}
                    </button>
                  ))}
                </div>
              )}
              <span style={{ color: "var(--ink-3)" }}>{positioned} / {plant.asset_count} positioned</span>
            </div>
          </div>
          <div className="flex-1 min-h-0 relative">
            {selectedArea && positioned > 0 ? (
              view === "compliance" ? (
                <CompliancePanel plantId={plant.id} />
              ) : view === "runway" ? (
                <PlantTriagePanel plantId={plant.id} assetCount={plant.asset_count} showSetupHint={false} />
              ) : view === "2d" ? (
                selectedArea.layout ? (
                  <div className="absolute inset-0" style={{ background: "var(--paper)" }}
                    onMouseMove={(e) => setMouse({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setHoverHotspot(null)}>
                    <PidCanvas
                      statusByTag={statusByTag}
                      hoveredId={hoverHotspot?.id}
                      onHotspotHover={setHoverHotspot}
                      onHotspotClick={onPidClick}
                    />
                    {hoverAsset && <PidHoverCard asset={hoverAsset} x={mouse.x} y={mouse.y} />}
                  </div>
                ) : (
                  <SitePlot2D
                    area={selectedArea}
                    assets={areaAssets}
                    onPick={(assetId) => navigate(`/assets/${assetId}`)}
                  />
                )
              ) : (
                <Suspense
                  fallback={
                    <div className="absolute inset-0 flex items-center justify-center font-mono text-xs" style={{ color: "var(--ink-3)" }}>
                      Loading deck…
                    </div>
                  }
                >
                  <Mockup3DScreen
                    statusByTag={statusByTag}
                    assets={areaAssets}
                    onPick={(tag) => {
                      const a = areaAssets.find((x) => x.tag.toLowerCase() === tag.toLowerCase());
                      if (a) navigate(`/assets/${a.id}`);
                    }}
                  />
                </Suspense>
              )
            ) : (
              /* No spatial view (nothing positioned / no plotted area) — the
                 primary pane still answers "what needs my attention?" with the
                 integrity triage instead of a dead placeholder. */
              <PlantTriagePanel plantId={plant.id} assetCount={plant.asset_count} />
            )}
          </div>
        </section>
      </main>
      </div>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────
function KpiCell({ label, value, crit }: { label: string; value: number; crit?: boolean }): JSX.Element {
  const hot = crit && value > 0;
  return (
    <div className="px-4 py-2 min-w-[100px] border-r last:border-r-0" style={{ borderColor: "var(--hairline)" }}>
      <div className="font-mono uppercase flex items-center gap-1" style={{ fontSize: 9.5, letterSpacing: ".22em", color: "var(--ink-4)" }}>
        {label}{hot && <AlertTriangle size={9} style={{ color: "var(--err)" }} />}
      </div>
      <div className="font-sans font-bold tabular-nums leading-tight" style={{ fontSize: 22, color: hot ? "var(--err)" : "var(--ink-1)" }}>{value.toLocaleString()}</div>
    </div>
  );
}

function CenterStatus({ children, tone }: { children: React.ReactNode; tone?: "err" }): JSX.Element {
  return (
    <div className="absolute inset-0 flex items-center justify-center" style={{ background: "var(--paper)" }}>
      <div className="font-mono uppercase" style={{ fontSize: 12, letterSpacing: ".22em", color: tone === "err" ? "var(--err)" : "var(--ink-3)" }}>{children}</div>
    </div>
  );
}
